import React, { useState, useEffect } from 'react';
import { getUserBookingsFromFirestore } from '../utils/firestoreBooking';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Invoice from '../components/Invoice';
import Footer from '../components/Footer';
import { toast } from 'react-toastify';
import './BookingsPage.css';

const BookingsPage = () => {
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoiceBooking, setInvoiceBooking] = useState(null);

  // Helper to open invoice in new tab for printing
  const handleDownloadInvoice = (booking) => {
    // Compose bookingDetails for Invoice
    const bookingDetails = {
      bookingName: booking.formData?.firstName
        ? `${booking.formData.firstName} ${booking.formData.lastName || ''}`.trim()
        : (booking.name || booking.guestName || currentUser?.displayName || 'Guest'),
      guestName: booking.formData?.firstName
        ? `${booking.formData.firstName} ${booking.formData.lastName || ''}`.trim()
        : (booking.name || booking.guestName || currentUser?.displayName || 'Guest'),
      bookingId: booking.id || booking.bookingId || booking.bookingData?.id || 'N/A',
      guestDetails: booking.formData ? {
        name: `${booking.formData.firstName} ${booking.formData.lastName || ''}`.trim(),
        email: booking.formData.email,
        phone: booking.formData.phone
      } : {
        name: booking.name || booking.guestName || currentUser?.displayName || 'Guest',
        email: booking.userEmail || currentUser?.email || '',
        phone: ''
      },
      checkInDate: booking.bookingData?.start
        ? new Date(booking.bookingData.start).toLocaleDateString()
        : (booking.checkIn ? new Date(booking.checkIn).toLocaleDateString() : 'N/A'),
      checkOutDate: booking.bookingData?.end
        ? new Date(booking.bookingData.end).toLocaleDateString()
        : (booking.checkOut ? new Date(booking.checkOut).toLocaleDateString() : 'N/A'),
      beds: booking.bookingData?.beds || booking.beds || booking.guests || 1,
      guests: booking.bookingData?.guests || booking.guests || booking.beds || 1,
      nights: booking.bookingData?.nights || booking.nights || (booking.checkIn && booking.checkOut ? Math.ceil((new Date(booking.checkOut)-new Date(booking.checkIn))/(1000*60*60*24)) : 1),
      bedNumbers: (Array.isArray(booking.selectedBeds) && booking.selectedBeds.length > 0)
        ? booking.selectedBeds.map(b => typeof b === 'object' ? (b.number || b.label || b.id || JSON.stringify(b)) : b).join(', ')
        : (Array.isArray(booking.bedNumbers) && booking.bedNumbers.length > 0
          ? booking.bedNumbers.join(', ')
          : '---'),
      ratePerNight: booking.bookingData?.pricePerBed || booking.pricePerBed || booking.ratePerNight || 699,
      taxRate: 0, // Set to 0 since convenience fee is already included in booking total
      extraCharges: booking.extraCharges || [],
      packageInfo: booking.packageInfo || booking.bookingData?.package || booking.selectedPackage,
      totalAmount: booking.total || booking.amount || booking.bookingData?.total || booking.totalAmount,
    };
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice - ${bookingDetails.bookingId}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
            .invoice-container { max-width: 800px; margin: 0 auto; }
          </style>
        </head>
        <body>
          <div id="invoice-root"></div>
        </body>
      </html>
    `);
    setTimeout(() => {
      const rootDiv = printWindow.document.getElementById('invoice-root');
      // Render Invoice to new window
      import('react-dom/client').then(({ createRoot }) => {
        const root = createRoot(rootDiv);
        root.render(<Invoice bookingDetails={bookingDetails} />);
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      });
    }, 100);
  };


  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    async function fetchBookings() {
      if (currentUser) {
        try {
          // Fetch bookings from Firestore
          const firestoreBookings = await getUserBookingsFromFirestore(currentUser.uid);
          
          // Also fetch from localStorage as backup
          const localBookings = JSON.parse(localStorage.getItem('userBookings') || '[]')
            .filter(booking => booking.userId === currentUser.uid);
          
          // Merge bookings, avoiding duplicates
          const allBookings = [...firestoreBookings];
          localBookings.forEach(localBooking => {
            const exists = firestoreBookings.some(fb => fb.id === localBooking.id);
            if (!exists) {
              allBookings.push(localBooking);
            }
          });
          
          // Sort by creation date (newest first)
          allBookings.sort((a, b) => {
            const dateA = new Date(a.createdAt || a.bookingData?.createdAt || 0);
            const dateB = new Date(b.createdAt || b.bookingData?.createdAt || 0);
            return dateB - dateA;
          });
          
          setBookings(allBookings);
          console.log('Loaded bookings:', allBookings.length, 'total');
          
        } catch (error) {
          console.error('Error fetching bookings from Firestore:', error);
          // Fallback to localStorage only
          try {
            const localBookings = JSON.parse(localStorage.getItem('userBookings') || '[]')
              .filter(booking => booking.userId === currentUser.uid);
            setBookings(localBookings);
            console.log('Loaded bookings from localStorage only:', localBookings.length);
          } catch (localError) {
            console.error('Error loading from localStorage:', localError);
            setBookings([]);
          }
        }
      }
    }
    fetchBookings();
  }, [currentUser]);
  
  const getStatusClass = (status) => {
    switch (status) {
      case 'Confirmed': return 'status-confirmed';
      case 'Pending': return 'status-pending';
      case 'Completed': return 'status-completed';
      default: return '';
    }
  };
  
  if (!currentUser) {
    navigate('/login');
    return null;
  }

  return (
    <>
      <Header />
      <div className="bookings-page">
        <div className="bookings-container">
          <div className="bookings-header">
            <h1>My Bookings</h1>
            <p>View and manage your reservations</p>
          </div>
          
          <div className="bookings-content">
            {bookings.length === 0 ? (
              <div className="no-bookings">
                <h3>You have no bookings yet</h3>
                <p>Start exploring our rooms and make your first reservation!</p>
                <button className="btn btn-primary" onClick={() => navigate('/')}>
                  Browse Rooms
                </button>
              </div>
            ) : (
              <div className="bookings-list">
                {bookings.map(booking => (
                  <div key={booking.id} className="booking-card">
                    <div className="booking-header">
                      <h3>{booking.bedType || booking.room || 'Dormitory'}</h3>
                      <span className={`booking-status ${getStatusClass(booking.status)}`}>
                        {booking.status}
                      </span>
                    </div>
                    <div className="booking-detail">
                      <span className="detail-label">Booking ID:</span>
                      <span className="detail-value">{booking.id || booking.bookingId || 'N/A'}</span>
                    </div>
                    <div className="booking-detail">
                      <span className="detail-label">Guest:</span>
                      <span className="detail-value">{booking.guestName || booking.name || (booking.formData?.firstName + ' ' + booking.formData?.lastName) || 'Guest'}</span>
                    </div>
                    <div className="booking-detail">
                      <span className="detail-label">Email:</span>
                      <span className="detail-value">{booking.guestEmail || booking.userEmail || booking.formData?.email || ''}</span>
                    </div>
                    <div className="booking-detail">
                      <span className="detail-label">Phone:</span>
                      <span className="detail-value">{booking.guestPhone || booking.formData?.phone || ''}</span>
                    </div>
                    
                    <div className="booking-details">
                      <div className="booking-detail">
                        <span className="detail-label">Check-in:</span>
                        <span className="detail-value">{booking.checkIn ? new Date(booking.checkIn).toLocaleDateString() : (booking.bookingData?.start ? new Date(booking.bookingData.start).toLocaleDateString() : '---')}</span>
                      </div>
                      <div className="booking-detail">
                        <span className="detail-label">Check-out:</span>
                        <span className="detail-value">{booking.checkOut ? new Date(booking.checkOut).toLocaleDateString() : (booking.bookingData?.end ? new Date(booking.bookingData.end).toLocaleDateString() : '---')}</span>
                      </div>
                      <div className="booking-detail">
                        <span className="detail-label">Beds:</span>
                        <span className="detail-value">{
                          booking.bookingData?.beds || booking.beds || booking.guests || 1
                        }</span>
                      </div>
                      <div className="booking-detail">
                        <span className="detail-label">Bed Numbers:</span>
                        <span className="detail-value">{
                          (Array.isArray(booking.selectedBeds) && booking.selectedBeds.length > 0)
                            ? booking.selectedBeds.map(b => typeof b === 'object' ? (b.number || b.label || b.id || JSON.stringify(b)) : b).join(', ')
                            : (Array.isArray(booking.bedNumbers) && booking.bedNumbers.length > 0
                                ? booking.bedNumbers.join(', ')
                                : '---')
                        }</span>
                      </div>
                      <div className="booking-detail">
                        <span className="detail-label">Total:</span>
                        <span className="detail-value">₹{
                          booking.bookingData?.total || booking.amount || booking.total || booking.totalPrice || 0
                        }</span>
                      </div>
                    </div>
                    
                    <div className="booking-actions">
                      <button className="btn btn-secondary" onClick={() => handleDownloadInvoice(booking)}>
                        Download Invoice
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default BookingsPage;
